import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Box,
  Button,
  ChevronDownIcon,
  ConditionalWrapper,
  Flex,
  Link,
  Popover,
  SearchField,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';

import {
  FilterButtonGrid,
  StyledButtonWrapper,
  StyledFilterList,
} from '../filter.styles';
import { DeselectAllListItem } from './deselectAllListItem';
import type { FilterPopoverProps } from './filterBarPopover.types';

export const FilterPopover = ({
  buttonText,
  children,
  disabled,
  disabledDeselectAll = false,
  isLoading,
  listLabel,
  totalResults,
  mobileItemsPerRow = 2,
  nested,
  onDeselect,
  rightAlign,
  startIcon,
  targetLabel,
  typeaheadOptions,
  variant,
}: FilterPopoverProps) => {
  const isMobile = useMobileMediaQuery({});
  const isSmallestMobile = useMobileMediaQuery({ size: 'sm', width: 'max' });

  const countLabel = pluralize({
    kilo: true,
    noun: variant,
    number: totalResults || 0,
  });

  const noResultsLabel = `${countLabel} ${!isMobile ? 'available' : ''}`;

  const showButtonLabel =
    totalResults === 0 ? noResultsLabel : `Show ${countLabel}`;

  return (
    <Popover
      contentRole="dialog"
      label={targetLabel}
      target={
        <Button
          label={buttonText}
          startIcon={startIcon}
          endIcon={!isMobile ? <ChevronDownIcon size="sm" /> : undefined}
          variant="tertiary"
          disabled={disabled}
          style={{ whiteSpace: 'nowrap' }}
        />
      }
      alignment={{
        horizontal: rightAlign ? 'right' : 'left',
        vertical: 'bottom',
      }}
      openOn={disabled ? 'disabled' : 'click'}
    >
      {({ closePopover, isOpen }: PopoverContextProps) => {
        const { label, onChange, query } = typeaheadOptions || {};

        if (onChange) {
          if (!isOpen && query) {
            query.length > 0 && onChange('');
          }
        }

        return (
          <Popover.Content padding={false}>
            {typeaheadOptions && (
              <SearchField
                label={`Search ${label}`}
                name={`Search ${label}`}
                defaultValue={query}
                onChange={onChange}
                disableMobileInputStyling
                mx={16}
                mt={16}
              />
            )}
            <StyledFilterList aria-label={listLabel}>
              <ConditionalWrapper
                condition={!nested && isMobile}
                wrapper={(children) => (
                  <Box p={24}>
                    <FilterButtonGrid
                      itemsPerRow={4}
                      mobileItemsPerRow={mobileItemsPerRow}
                    >
                      {children}
                    </FilterButtonGrid>
                  </Box>
                )}
              >
                {children}
              </ConditionalWrapper>

              {!disabledDeselectAll &&
                (isMobile ? (
                  <StyledButtonWrapper>
                    <Flex
                      gap={16}
                      flexDirection={isSmallestMobile ? 'column' : 'row'}
                      alignItems="center"
                      py={16}
                      px={24}
                    >
                      <Flex grow pl={12}>
                        <Link onClick={onDeselect}>
                          <Typography>Deselect all</Typography>
                        </Link>
                      </Flex>

                      <Button
                        label={showButtonLabel}
                        full
                        onClick={closePopover}
                        data-testid={`show-${variant}-button`}
                        loading={isLoading || totalResults === undefined}
                        disabled={totalResults === 0}
                      />
                    </Flex>
                  </StyledButtonWrapper>
                ) : (
                  <DeselectAllListItem onClick={onDeselect} />
                ))}
            </StyledFilterList>
          </Popover.Content>
        );
      }}
    </Popover>
  );
};
